import {t, useGlobalSetting} from "@/lib/utils";
import type {SelectItemKey} from "vuetify/lib/util";
import {DataTableCompareFunction, HeaderCellProps} from "vuetify/lib/components/VDataTable/types";
import {Header} from "@/lib/types";
import IsSafetySelect from "@/components/inputs/isSafetySelect.vue";
import DriveTypeSelector from "@/components/inputs/motorTypeSelector.vue";
import {supabase} from "@/api";

export enum driveType {
    ANTIREB = "ANTRIEB",
    HEIZUNG = "HEIZUNG",
    NONE = "NONE",
}

export interface Drive {
    id: string;
    asset_id: string;
    description_primary: string;
    description_secondary: string;
    type: string;
    type_comment: string;
    is_safety: boolean;
    safety_code: string;
    has_belt: boolean;
    belt_comment: string;
    has_brake: boolean;
    brake_comment: string;
    electrical_room: string;
    electrical_enclosure: string;
    location_level: string | null;
    location_row: string | null;
    location_col: string | null;
    position: string | null;
    system_voltage: number;
    system_voltage_unit: string;
    system_current: number;
    system_current_unit: string;
    system_power: number;
    system_power_unit: string;
    system_nominal_speed: number;
    system_nominal_speed_unit: string;
    process_controller: string;
    process_network_address: string;
    manufacturer: string;
    manufacturer_id: string;
    maximo_id: string;
    drive_number: string;
    has_power_block: boolean;
    power_block_manufacture_id: string;
    power_block_maximo_id: string;
    comment: string;
    has_power_calculation: boolean;
    created_at: string;
    created_by: string;
    updated_at: string;
    updated_by: string;
}

export interface ListDrive {
    asset_id: string;
    type_comment: string;
    description_primary: string;
    is_safety: boolean;
    location_row: string | null;
    location_col: string | null;
    location_level: string | null;
    system_power: number;
    system_power_unit: string;
}

export const DEFAULT_DRIVE: Drive = {
    id: '',
    asset_id: '',
    description_primary: '',
    description_secondary: '',
    type: '',
    type_comment: '',
    is_safety: false,
    safety_code: '',
    has_belt: false,
    belt_comment: '',
    has_brake: false,
    brake_comment: '',
    electrical_room: '',
    electrical_enclosure: '',
    location_level: '-1',
    location_row: '-1',
    location_col: '-1',
    position: '',
    system_voltage: -1,
    system_voltage_unit: '',
    system_current: -1,
    system_current_unit: '',
    system_power: -1,
    system_power_unit: '',
    system_nominal_speed: -1,
    system_nominal_speed_unit: '',
    process_controller: '',
    process_network_address: '',
    manufacturer: '',
    manufacturer_id: '',
    maximo_id: '',
    drive_number: '',
    has_power_block: false,
    power_block_manufacture_id: '',
    power_block_maximo_id: '',
    comment: '',
    has_power_calculation: false,
    created_at: '',
    created_by: '',
    updated_at: '',
    updated_by: ''
};

export type MotorTableHeader<T = Record<string, any>> = {
    key: 'data-table-group' | 'data-table-select' | 'data-table-expand' | (string & {});
    value?: SelectItemKey<T>;
    title?: string;
    fixed?: boolean;
    align?: 'start' | 'end' | 'center';
    width?: number | string;
    minWidth?: number | string;
    maxWidth?: number | string;
    nowrap?: boolean;
    headerProps?: Record<string, any>;
    cellProps?: HeaderCellProps;
    sortable?: boolean;
    sort?: DataTableCompareFunction;
    sortRaw?: DataTableCompareFunction;
    disabled?: boolean;
    canBeFiltered?: boolean;
    icon?: string;
    checkbox?: boolean;
    tab: string;
    custom?: boolean;
};

export interface compressedMotor {
    id: string;
    description: string;
    drive_type: string;
    type: string;
    is_safety: boolean;
    position: string;
    system_power: number;
}

export const global_headers: Header[] = [
    {title: 'AKZ / TAG', key: 'asset_id', align: 'center', icon: 'mdi-tag', tab: 'general'},
    {title: 'Bezeichnung', key: 'description_primary', align: 'center', icon: 'mdi-text-box-outline', tab: 'general'},
    {title: 'Antriebstyp', key: 'description_secondary', align: 'center', icon: 'mdi-engine-outline', tab: 'general'},
    {title: 'Kreistyp', key: 'type', align: 'center', icon: 'mdi-vector-circle', tab: 'general', filterComponent: DriveTypeSelector},
    {title: 'Riemenantrieb', key: 'has_belt', checkbox: true, align: 'center', icon: 'mdi-square-rounded-outline', tab: 'general'},
    {title: 'Bremse', key: 'has_brake', checkbox: true, align: 'center', icon: 'mdi-car-brake-alert', tab: 'general'},
    {title: 'OKZ', key: 'electrical_enclosure', align: 'center', icon: 'mdi-file-cabinet', tab: 'electrical'},
    {title: 'E-Raum', key: 'electrical_room', align: 'center', icon: 'mdi-door', tab: 'electrical'},
    /*{title: 'Ebene', key: 'location_level', align: 'center', icon: 'mdi-axis-z-arrow', tab: 'general'},
    {title: 'Achse', key: 'location_col', align: 'center', icon: 'mdi-axis-x-arrow', tab: 'general'},
    {title: 'Säule', key: 'location_row', align: 'center', icon: 'mdi-axis-y-arrow', tab: 'general'},*/
    {title: 'Position', key: 'position', align: 'center', icon: 'mdi-map-marker', tab: 'general', canBeFiltered: false},
    {title: 'Spannung', key: 'system_voltage', align: 'center', icon: 'mdi-alpha-u-circle', tab: 'electrical'},
    {title: 'Leistung', key: 'system_power', align: 'center', icon: 'mdi-alpha-p-circle', tab: 'electrical'},
    {title: 'Stromstärke', key: 'system_current', align: 'center', icon: 'mdi-alpha-a-circle', tab: 'electrical'},
    {title: 'Nenndrehzahl', key: 'system_nominal_speed', align: 'center', icon: 'mdi-speedometer', tab: 'electrical'},
    {title: 'Prozessstation / CPU', key: 'process_controller', align: 'center', icon: 'mdi-server', tab: 'general'},
    {title: 'IP Adresse', key: 'process_network_address', align: 'center', icon: 'mdi-ip-network', tab: 'general'},
    {title: 'Herstellernummer', key: 'manufacturer_id', align: 'center', icon: 'mdi-barcode', tab: 'misc'},
    {title: 'Artikelnummer Maximo', key: 'maximo_id', align: 'center', icon: 'mdi-barcode-scan', tab: 'misc'},
    {title: 'Motorhersteller', key: 'manufacturer', align: 'center', icon: 'mdi-factory', tab: 'misc'},
    {title: 'M-Nummer', key: 'drive_number', align: 'center', icon: 'mdi-numeric', tab: 'misc'},
    {title: 'Herstellernummer Powerblock Leistungsteil', key: 'power_block_manufacture_id', align: 'center', icon: 'mdi-barcode', tab: 'misc'},
    {title: 'Artikelnummer Powerblock Maximo', key: 'power_block_maximo_id', align: 'center', icon: 'mdi-barcode-scan', tab: 'misc'},
    {title: 'Bemerkung', key: 'comment', align: 'center', icon: 'mdi-comment-text-outline', tab: 'general'},
    {title: 'Leistungsberechnung vorhanden', key: 'has_power_calculation', checkbox: true, align: 'center', icon: 'mdi-calculator', tab: 'misc'},
    {title: 'Safety', key: 'is_safety', checkbox: true, align: 'center', icon: 'mdi-shield-check', tab: 'safety', filterComponent: IsSafetySelect},
    {title: 'F-Zieladdresse', key: 'safety_code', align: 'center', icon: 'mdi-alpha-f-circle', tab: 'safety'},
    {title: 'DIL_Schalterstellung', key: 'dil', align: 'center', icon: 'mdi-alpha-f-circle', tab: 'safety', canBeFiltered: false},
];

/*export const global_headers: Header[] = [
    {title: 'AKZ / TAG', key: 'assetId', align: 'center', icon: 'mdi-tag', tab: 'general'},
    {title: 'Bezeichnung', key: 'descriptionPrimary', align: 'center', icon: 'mdi-text-box-outline', tab: 'general'},
    {title: 'Antriebstyp', key: 'descriptionSecondary', align: 'center', icon: 'mdi-engine-outline', tab: 'general'},
    {title: 'Kreistyp', key: 'type', align: 'center', icon: 'mdi-vector-circle', tab: 'general', filterComponent: DriveTypeSelector},
    {title: 'Riemenantrieb', key: 'hasBelt', checkbox: true, align: 'center', icon: 'mdi-square-rounded-outline', tab: 'general'},
    {title: 'Bremse', key: 'hasBrake', checkbox: true, align: 'center', icon: 'mdi-car-brake-alert', tab: 'general'},
    {title: 'OKZ', key: 'electricalEnclosure', align: 'center', icon: 'mdi-file-cabinet', tab: 'electrical'},
    {title: 'E-Raum', key: 'electricalRoom', align: 'center', icon: 'mdi-door', tab: 'electrical'},
    /!*{title: 'Ebene', key: 'locationLevel', align: 'center', icon: 'mdi-axis-z-arrow', tab: 'general'},
    {title: 'Achse', key: 'locationCol', align: 'center', icon: 'mdi-axis-x-arrow', tab: 'general'},
    {title: 'Säule', key: 'locationRow', align: 'center', icon: 'mdi-axis-y-arrow', tab: 'general'},*!/
    {title: 'Position', key: 'position', align: 'center', icon: 'mdi-map-marker', tab: 'general', canBeFiltered: false},
    {title: 'Spannung', key: 'systemVoltage', align: 'center', icon: 'mdi-alpha-u-circle', tab: 'electrical'},
    {title: 'Leistung', key: 'systemPower', align: 'center', icon: 'mdi-alpha-p-circle', tab: 'electrical'},
    {title: 'Stromstärke', key: 'systemCurrent', align: 'center', icon: 'mdi-alpha-a-circle', tab: 'electrical'},
    {title: 'Nenndrehzahl', key: 'systemNominalSpeed', align: 'center', icon: 'mdi-speedometer', tab: 'electrical'},
    {title: 'Prozessstation / CPU', key: 'processController', align: 'center', icon: 'mdi-server', tab: 'general'},
    {title: 'IP Adresse', key: 'processNetworkAddress', align: 'center', icon: 'mdi-ip-network', tab: 'general'},
    {title: 'Herstellernummer', key: 'manufacturerId', align: 'center', icon: 'mdi-barcode', tab: 'misc'},
    {title: 'Artikelnummer Maximo', key: 'maximoId', align: 'center', icon: 'mdi-barcode-scan', tab: 'misc'},
    {title: 'Motorhersteller', key: 'manufacturer', align: 'center', icon: 'mdi-factory', tab: 'misc'},
    {title: 'M-Nummer', key: 'driveNumber', align: 'center', icon: 'mdi-numeric', tab: 'misc'},
    {title: 'Herstellernummer Powerblock Leistungsteil', key: 'powerBlockManufactureId', align: 'center', icon: 'mdi-barcode', tab: 'misc'},
    {title: 'Artikelnummer Powerblock Maximo', key: 'powerBlockMaximoId', align: 'center', icon: 'mdi-barcode-scan', tab: 'misc'},
    {title: 'Bemerkung', key: 'comment', align: 'center', icon: 'mdi-comment-text-outline', tab: 'general'},
    {title: 'Leistungsberechnung vorhanden', key: 'hasPowerCalculation', checkbox: true, align: 'center', icon: 'mdi-calculator', tab: 'misc'},
    {title: 'Safety', key: 'isSafety', checkbox: true, align: 'center', icon: 'mdi-shield-check', tab: 'safety', filterComponent: IsSafetySelect},
    {title: 'F-Zieladdresse', key: 'safetyCode', align: 'center', icon: 'mdi-alpha-f-circle', tab: 'safety'},
    {title: 'DIL_Schalterstellung', key: 'dil', align: 'center', icon: 'mdi-alpha-f-circle', tab: 'safety', canBeFiltered: false},
]*/

/*export interface Drive {
    id: number;                          // DB: SERIAL PRIMARY KEY
    assetId: string;                     // DB: asset_id (AKZ)

    // Description
    descriptionPrimary: string | null;   // DB: description_primary
    descriptionSecondary: string | null; // DB: description_secondary

    // Type
    driveType: string | null;            // DB: drive_type

    // Safety
    isSafety: boolean;                   // DB: is_safety
    safetyCode: string | null;           // DB: safety_code

    // Features
    hasBelt: boolean;                    // DB: has_belt
    hasBrake: boolean;                   // DB: has_brake

    // Location
    locationRow: string | null;          // DB: location_row
    locationCol: string | null;          // DB: location_col
    locationLevel: string | null;        // DB: location_level
    position: string | null;

    // Technical Data
    systemPower: number | null;              // DB: power_kw
    systemVoltage: number | null;             // DB: voltage_v
    systemCurrent: number | null;             // DB: current_a
    systemNominalSpeed: number | null;             // DB: speed_rpm

    systemPowerUnit: number | null;
    systemVoltageUnit: number | null;
    systemCurrentUnit: number | null;
    systemNominalSpeedUnit: number | null;

    // Protection
    protectionClass: string | null;      // DB: protection_class
    ipRating: string | null;             // DB: ip_rating

    // Additional
    manufacturer: string | null;         // DB: manufacturer
    model: string | null;                // DB: model
    serialNumber: string | null;         // DB: serial_number
    installationDate: string | null;     // DB: installation_date
    lastMaintenance: string | null;      // DB: last_maintenance

    // Extra data for unmapped fields
    extraData: Record<string, any>;      // DB: extra_data JSONB

    // Timestamps
    createdAt?: string;                  // DB: created_at
    updatedAt?: string;                  // DB: updated_at
}

export const DEFAULT_DRIVE: Drive = {
    id: -1,
    assetId: '',
    descriptionPrimary: null,
    descriptionSecondary: null,
    driveType: null,
    isSafety: false,
    safetyCode: null,
    hasBelt: false,
    hasBrake: false,
    locationRow: null,
    locationCol: null,
    locationLevel: null,
    position: null,
    systemPower: null,
    systemVoltage: null,
    systemCurrent: null,
    systemNominalSpeed: null,
    systemPowerUnit: null,
    systemVoltageUnit: null,
    systemCurrentUnit: null,
    systemNominalSpeedUnit: null,
    protectionClass: null,
    ipRating: null,
    manufacturer: null,
    model: null,
    serialNumber: null,
    installationDate: null,
    lastMaintenance: null,
    extraData: {},
};

export type MotorTableHeader<T = Record<string, any>> = {
    key: 'data-table-group' | 'data-table-select' | 'data-table-expand' | (string & {});
    value?: SelectItemKey<T>;
    title?: string;
    fixed?: boolean;
    align?: 'start' | 'end' | 'center';
    width?: number | string;
    minWidth?: number | string;
    maxWidth?: number | string;
    nowrap?: boolean;
    headerProps?: Record<string, any>;
    cellProps?: HeaderCellProps;
    sortable?: boolean;
    sort?: DataTableCompareFunction;
    sortRaw?: DataTableCompareFunction;
    disabled?: boolean;
    canBeFiltered?: boolean;
    icon?: string;
    checkbox?: boolean;
    tab: string;
    custom?: boolean;
};

export interface CompressedDrive {
    id: number;
    assetId: string;
    descriptionPrimary: string | null;
    driveType: string | null;
    isSafety: boolean;
    powerKw: number | null;
}

export const global_headers: Header[] = [
    {title: 'AKZ / TAG', key: 'assetId', align: 'center', icon: 'mdi-tag', tab: 'general'},
    {title: 'Bezeichnung', key: 'descriptionPrimary', align: 'center', icon: 'mdi-text-box-outline', tab: 'general'},
    {title: 'Antriebstyp', key: 'descriptionSecondary', align: 'center', icon: 'mdi-engine-outline', tab: 'general'},
    {title: 'Kreistyp', key: 'driveType', align: 'center', icon: 'mdi-vector-circle', tab: 'general', filterComponent: DriveTypeSelector},
    {title: 'Riemenantrieb', key: 'hasBelt', checkbox: true, align: 'center', icon: 'mdi-square-rounded-outline', tab: 'general'},
    {title: 'Bremse', key: 'hasBrake', checkbox: true, align: 'center', icon: 'mdi-car-brake-alert', tab: 'general'},
    {title: 'Ebene', key: 'locationLevel', align: 'center', icon: 'mdi-axis-z-arrow', tab: 'general'},
    {title: 'Achse', key: 'locationCol', align: 'center', icon: 'mdi-axis-x-arrow', tab: 'general'},
    {title: 'Säule', key: 'locationRow', align: 'center', icon: 'mdi-axis-y-arrow', tab: 'general'},
    {title: 'Spannung', key: 'systemVoltage', align: 'center', icon: 'mdi-alpha-u-circle', tab: 'electrical'},
    {title: 'Leistung (kW)', key: 'systemPower', align: 'center', icon: 'mdi-alpha-p-circle', tab: 'electrical'},
    {title: 'Stromstärke', key: 'systemCurrent', align: 'center', icon: 'mdi-alpha-a-circle', tab: 'electrical'},
    {title: 'Nenndrehzahl', key: 'systemNominalSpeed', align: 'center', icon: 'mdi-speedometer', tab: 'electrical'},
    {title: 'Schutzart', key: 'protectionClass', align: 'center', icon: 'mdi-shield', tab: 'electrical'},
    {title: 'IP-Schutzart', key: 'ipRating', align: 'center', icon: 'mdi-water-off', tab: 'electrical'},
    {title: 'Hersteller', key: 'manufacturer', align: 'center', icon: 'mdi-factory', tab: 'misc'},
    {title: 'Modell', key: 'model', align: 'center', icon: 'mdi-tag-text', tab: 'misc'},
    {title: 'Seriennummer', key: 'serialNumber', align: 'center', icon: 'mdi-barcode', tab: 'misc'},
    {title: 'Installationsdatum', key: 'installationDate', align: 'center', icon: 'mdi-calendar', tab: 'misc'},
    {title: 'Letzte Wartung', key: 'lastMaintenance', align: 'center', icon: 'mdi-wrench-clock', tab: 'misc'},
    {title: 'Safety', key: 'isSafety', checkbox: true, align: 'center', icon: 'mdi-shield-check', tab: 'safety', filterComponent: IsSafetySelect},
    {title: 'F-Zieladdresse', key: 'safetyCode', align: 'center', icon: 'mdi-alpha-f-circle', tab: 'safety'},
]*/

export const getMotorFromId = async (id: string): Promise<Drive> => {
    useGlobalSetting().loadingMessage = t("loading.motorInfo")
    useGlobalSetting().isLoading = true;
    const {data, error} = await supabase.from('drives').select('*').eq('asset_id', id).single();
    useGlobalSetting().isLoading = false;
    if (error) {
        console.error('Fehler beim Laden des Motors:', error)
        return DEFAULT_DRIVE
    }
    return data as Drive;
}

export const checkMotorId = async (id: string): Promise<boolean> => {
    const {getDrivePosition} = await import('@/api/drives')
    return (await getDrivePosition(id)) !== null
}
